import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { screenTagService } from "../api/services/screenTagService";
import {
  ScreenTagFilters,
  ScreenTagsResponse,
  UpdateScreenTagPayload,
} from "../schemas/screenTagSchema";

export const useScreenTags = (
  filters: ScreenTagFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: screenTags,
    isLoading: isScreenTagsLoading,
    refetch,
  } = useQuery<ScreenTagsResponse, Error>({
    queryKey: ["screenTags", filters],
    queryFn: () => screenTagService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createScreenTagMutation = useMutation({
    mutationFn: screenTagService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["screenTags"] });
      queryClient.invalidateQueries({ queryKey: ["screensTagOptions"] });
      toast.success("Screen Tag created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create Screen Tag");
    },
  });

  const updateScreenTagMutation = useMutation({
    mutationFn: screenTagService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["screenTags"] });
      queryClient.invalidateQueries({ queryKey: ["screensTagOptions"] });
      toast.success("Screen Tag updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update Screen Tag");
    },
  });

  const deleteScreenTagMutation = useMutation({
    mutationFn: screenTagService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["screenTags"] });
      queryClient.invalidateQueries({ queryKey: ["screensTagOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Screen Tag deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete Screen Tag");
    },
  });

  const deleteScreenTags = (ids: string[]) => {
    deleteScreenTagMutation.mutate(ids);
  };

  return {
    screenTags,
    refetchScreenTags: refetch,
    createScreenTag: createScreenTagMutation.mutate,
    updateScreenTag: updateScreenTagMutation.mutate,
    deleteScreenTags,
    isLoading:
      isScreenTagsLoading ||
      createScreenTagMutation.isPending ||
      updateScreenTagMutation.isPending ||
      deleteScreenTagMutation.isPending,
  };
};
